/**
 * The plan's own ⋯ menu in the builder's bar: which of the three acts this plan may be offered, and
 * what each one asks before it is taken.
 *
 * Owner, 17 September 2026: the builder had no way to delete a plan and no way to end one, and the
 * menu that carried its two harmless acts — rename, and the category/cover panel — opened only on a
 * right-click of the title, which nothing on screen suggested. So the menu gets a visible trigger
 * and two more entries.
 *
 * ── The rule the calendar's own plan menu already states, and this keeps ─────────────────────
 * An act the server would refuse is ABSENT rather than dimmed, because this app does not own the
 * rule and cannot word the refusal. Same as `assigned-plan-calendar/model/planActs`.
 *
 * ── Why this is not `planActs` ───────────────────────────────────────────────────────────────
 * That module decides six acts from the calendar's own grid — cycle extents, a cursor, sessions
 * dated ahead of today — and none of those exist here: the builder holds a plan document, not a
 * schedule. What IS shared is the copy, and the copy is what is reused: every string below comes
 * from the `planCalendar.planAct*` keys the calendar already ships, so one act has one wording
 * wherever a trainer meets it. Reimplementing the sentences here is exactly the duplication the
 * repository's fixture rule exists to prevent.
 *
 * ── Which of the two endings is NOT decided here ─────────────────────────────────────────────
 * `planEnding` owns that, in `entities/plan-draft`, and the calendar's plan menu calls the same
 * function. It used to be decided twice from two different facts, and the two disagreed on a plan
 * whose last session had passed with a skip still open — one click apart, on one assignment, both
 * irreversible. This module hands over the one named fact, `PlanFork.hasPlanLeft`; that type is
 * where the two modes' readings of it are written down.
 *
 * ── Delete and end are mutually exclusive by construction, and that is the honest shape ──────
 * A plan is either a LIBRARY entry — nobody is training it, so it can be deleted and there is
 * nothing to end — or a FORK opened on one client, which is somebody's running plan and is ended
 * rather than deleted. Deleting a fork from here would take a client's plan, its history and its
 * progress out from under them with no sentence on screen able to describe it honestly, so it is
 * not offered. The plan is ended instead, which is what both the mobile app and the calendar do.
 *
 * ── Deleting a library entry says how many people it would orphan ────────────────────────────
 * The backend deletes it whatever the answer: the forks are separate templates and survive, but the
 * line back to their source does not, so the plans list stops being able to group them. That is a
 * real consequence and the confirm states it rather than discovering it afterwards.
 *
 * No React, no DOM, no query hook, no i18next — `t` arrives as a parameter.
 */

import { planEnding, type PlanFork, type PlanTemplateAssignments } from '~/entities/plan-draft';

export type Translate = (key: string, values?: Record<string, string | number>) => string;

/** The three acts this menu may carry, beyond the two the bar already had. */
export type PlanLifecycleAct = 'endEarly' | 'complete' | 'delete';

export interface PlanLifecycleEntry {
  readonly act: PlanLifecycleAct;
  readonly label: string;
  /** Every one of these is irreversible. The surface marks them; nothing else on the menu is marked. */
  readonly destructive: true;
}

/** What a chosen act asks before it is taken. Shaped for `shared/ui/ConfirmDialog`. */
export interface PlanLifecyclePrompt {
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly destructiveConfirm: true;
}

export interface PlanLifecycleInput {
  /** What the trainer's assignment list says about this template. */
  readonly assignments: PlanTemplateAssignments;
  /** Whether the plan is a library entry. A fork is the other case; there is no third. */
  readonly isLibraryEntry: boolean;
  /** True while the read behind `assignments` is still open — nothing is offered on a guess. */
  readonly pending: boolean;
}

const CAL = 'web:planCalendar';
const KEY = 'web:planBuilder.planMenu';

/** Terminal statuses. A plan already over has nothing left to end. */
const OVER: ReadonlySet<string> = new Set(['Completed', 'Cancelled']);

function endingAct(fork: PlanFork): PlanLifecycleAct | null {
  if (OVER.has(fork.status?.trim() ?? '')) return null;
  return planEnding(fork.hasPlanLeft);
}

/**
 * The menu's lifecycle entries, in the order they are drawn. Empty is a normal answer — a fork whose
 * plan is already over offers neither ending, and offers no deletion either.
 */
export function planLifecycleMenu(
  input: PlanLifecycleInput,
  t: Translate,
): readonly PlanLifecycleEntry[] {
  // Nothing is offered while the read is open. An entry that appears a second later under a pointer
  // already moving toward the menu is worse than an entry that was never there.
  if (input.pending) return [];

  const { fork } = input.assignments;
  if (fork !== null) {
    const act = endingAct(fork);
    return act === null ? [] : [{ act, label: t(`${CAL}.planActions.${act}`), destructive: true }];
  }

  // A plan that is neither a fork nor a library entry is a draft the trainer has not saved as
  // either; there is nothing on the server to delete.
  if (!input.isLibraryEntry) return [];
  return [{ act: 'delete', label: t(`${KEY}.delete`), destructive: true }];
}

export interface PlanLifecyclePromptInput {
  /** Live assignments forked from this template. Read for `delete` and nothing else. */
  readonly followerCount: number;
  /**
   * Whether the plan books no dates. `endEarly`'s ordinary sentence describes sessions leaving a
   * calendar, and a self-paced plan has none — the calendar ships a second sentence for exactly
   * that, and this is the fact that picks it. R8.
   */
  readonly isSelfPaced: boolean;
}

/**
 * What the chosen act asks. The two endings quote the calendar's own sentences; the deletion is this
 * screen's, because the mobile app's `planTemplates.detail.deleteConfirm` says only "this cannot be
 * undone" and says nothing about the clients whose plans point at the template.
 */
export function planLifecyclePrompt(
  act: PlanLifecycleAct,
  input: PlanLifecyclePromptInput,
  t: Translate,
): PlanLifecyclePrompt {
  const cancelLabel = t(`${CAL}.act.close`);

  if (act === 'delete') {
    return {
      title: t(`${KEY}.deleteTitle`),
      description:
        input.followerCount === 0
          ? t(`${KEY}.deleteBody`)
          : t(`${KEY}.deleteBodyFollowed`, { count: input.followerCount }),
      confirmLabel: t(`${KEY}.deleteConfirm`),
      cancelLabel,
      destructiveConfirm: true,
    };
  }

  return {
    title: t(`${CAL}.planAct.${act}.title`),
    description:
      act === 'endEarly' && input.isSelfPaced
        ? t(`${CAL}.planAct.endEarly.bodySelfPaced`)
        : t(`${CAL}.planAct.${act}.body`),
    confirmLabel: t(`${CAL}.planAct.${act}.confirm`),
    cancelLabel,
    destructiveConfirm: true,
  };
}
